// ============================
// File: src/features/requirements/RequirementsBuilder.jsx
// ============================
import React, { useEffect, useMemo, useRef, useState } from "react";
import { QueryClient, QueryClientProvider, useMutation } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import {
  UncontrolledTreeEnvironment,
  StaticTreeDataProvider,
  Tree,
  InteractionMode,
} from "react-complex-tree";
import "react-complex-tree/lib/style-modern.css";

import { getRequirements, runRequirementsAI, saveRequirementsArtifacts } from "./api/requirementsClient";
const cx = (...c) => c.filter(Boolean).join(" ");

function ToolbarButton({ children, onClick, variant = "primary", disabled }) {
  const base = "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2";
  const styles = {
    primary: "bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-600",
    outline: "border border-gray-300 bg-var(--cares-line-card) hover:bg-gray-50 text-gray-900 focus:ring-gray-400",
    ghost: "text-gray-700 hover:bg-gray-100 focus:ring-gray-300",
  };
  return (
    <button onClick={onClick} disabled={disabled} className={cx(base, styles[variant], disabled && "opacity-60 cursor-not-allowed")}>
      {children}
    </button>
  );
}

function Card({ title, subtitle, right, children, className }) {
  return (
    <section className={cx("rounded-2xl border border-gray-200 bg-white shadow-sm", className)}>
      {(title || right || subtitle) && (
        <header className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
          </div>
          {right}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

const MIN_NFR_SET = ["availability", "performance", "security", "logging"];

/* -------------------- helpers -------------------- */
const download = (name, text, type = "text/plain") => {
  const blob = new Blob([text], { type });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
};

const toCSV = (rows) => {
  if (!rows?.length) return "";
  const keys = Object.keys(rows[0]);
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [keys.join(","), ...rows.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\n");
};

/* -------------------- Left: Interview Console -------------------- */
function InterviewConsole({ demandId, onIngestAI }) {
  const [input, setInput] = React.useState("");
  const [chat, setChat] = React.useState([]); // {role, content}
  const scrollRef = React.useRef(null);

  // Keep scroll at bottom when chat updates
  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat]);

  const askMut = useMutation({
    mutationFn: ({ message }) => runRequirementsAI(demandId, message, chat),
    onSuccess: (ai) => {
      setChat((c) => [...c, { role: "assistant", content: ai.reply }]);
      onIngestAI?.(ai.suggestions || { fr: ai.fr, nfr: ai.nfr });
    },
    onError: () => {
      setChat((c) => [
        ...c,
        { role: "assistant", content: "(offline) capturing notes and updating the spec locally." },
      ]);
      onIngestAI?.({ notes: input });
    },
  });

  const send = async () => {
    const message = input.trim();
    if (!message) return;
    setChat((c) => [...c, { role: "user", content: message }]);
    setInput("");
    await askMut.mutateAsync({ message });
  };

  return (
    <div className="flex flex-col h-full">
      <div ref={scrollRef}
      className="mb-4 flex-1 overflow-auto space-y-3"
      style={{maxHeight: '400px', minHeight: '300px'}}
      >
        {chat.length === 0 && (
          <div className="rounded-lg bg-blue-50 p-4 text-sm text-gray-600">
            💡 Ask about FRs, NFRs, acceptance criteria, integrations, or scope.
          </div>
        )}
        {chat.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[80%] rounded-lg px-4 py-2 ${
                m.role === "user" ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-900"
              }`}
            >
              <div className="whitespace-pre-wrap text-sm">{m.content}</div>
              <div className={`mt-1 text-xs ${m.role === "user" ? "text-indigo-200" : "text-gray-500"}`}>
                {m.role === "user" ? "You" : "AI"} • {new Date().toLocaleTimeString()}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="Ask a question or capture stakeholder input…"
          rows={3}
          className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          aria-label="Interview prompt"
        />
        <button
          onClick={send}
          disabled={!input.trim() || askMut.isPending}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {askMut.isPending ? "Sending..." : "Send"}
        </button>
      </div>
    </div>
  );
}


/* -------------------- Right: Spec Builder Tabs -------------------- */
function FRTable({ frs, setFRs }) {
  const [textFR, setTextFR] = useState("");
  const addFR = () => {
    if (!textFR.trim()) return;
    setFRs((arr) => [...arr, { id: crypto.randomUUID(), title: textFR.trim(), ac: [""], source: "AI/Interview" }]);
    setTextFR("");
  };
  const updateFR = (idx, patch) => setFRs((arr) => arr.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeFR = (idx) => setFRs((arr) => arr.filter((_, i) => i !== idx));

  return (
  <div className="space-y-3">
    <div className="flex gap-2">
      <input
        className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        value={textFR}
        onChange={(e) => setTextFR(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            addFR();
          }
        }}
        placeholder="Add Functional Requirement…"
        aria-label="Add functional requirement"
      />
      <ToolbarButton variant="primary" onClick={addFR}>
        + Add FR
      </ToolbarButton>
    </div>

    <div className="space-y-3">
      {frs.map((fr, idx) => (
        <div key={fr.id} className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="mb-3 flex items-center gap-3">
            <span className="text-sm font-semibold text-gray-700">FR-{idx + 1}</span>
            <input
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={fr.title}
              onChange={(e) => updateFR(idx, { title: e.target.value })}
              aria-label={`FR ${idx + 1} title`}
            />
            <button
              onClick={() => removeFR(idx)}
              className="text-sm text-red-600 hover:text-red-700"
              aria-label="Remove FR"
            >
              Remove
            </button>
          </div>

          <div className="space-y-2 ml-4">
            <div className="text-xs font-medium text-gray-600">Acceptance Criteria</div>
            {fr.ac.map((ac, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={ac}
                  onChange={(e) => updateFR(idx, { ac: fr.ac.map((a, j) => (j === i ? e.target.value : a)) })}
                  placeholder="Acceptance criterion..."
                  aria-label={`FR ${idx + 1} AC ${i + 1}`}
                />
                <button
                  className="text-sm text-gray-600 hover:text-red-600"
                  onClick={() => updateFR(idx, { ac: fr.ac.filter((_, j) => j !== i) })}
                  aria-label="Remove AC"
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              className="text-sm text-indigo-600 hover:text-indigo-700"
              onClick={() => updateFR(idx, { ac: [...fr.ac, ""] })}
            >
              + Add Acceptance Criterion
            </button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

}

function NFRTable({ nfrs, setNfrs }) {
  const [text, setText] = useState("");
  const add = () => { if (!text.trim()) return; setNfrs((a) => [...a, { id: crypto.randomUUID(), name: text.trim(), value: "TBD" }]); setText(""); };
  const update = (idx, patch) => setNfrs((arr) => arr.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const remove = (idx) => setNfrs((arr) => arr.filter((_, i) => i !== idx));
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <input
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder="Add Non-Functional Requirement…"
        />
        <ToolbarButton variant="primary" onClick={add}>
          + Add NFR
        </ToolbarButton>
      </div>
      <div className="grid gap-3">
        {nfrs.map((n, idx) => (
          <div key={n.id} className="grid grid-cols-[200px_1fr_auto] gap-3 items-center rounded-lg border border-gray-200 bg-white p-3">
            <input
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={n.name}
              onChange={(e) => update(idx, { name: e.target.value })}
              placeholder="Category"
            />
            <input
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={n.value}
              onChange={(e) => update(idx, { value: e.target.value })}
              placeholder="Requirement value"
            />
            <button
              onClick={() => remove(idx)}
              className="text-sm text-red-600 hover:text-red-700"
            >
              Remove
            </button>
          </div>
        ))}
      </div>
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
        <p className="text-sm text-amber-800">
          ⚠️ Minimum NFR set expected: availability, performance, security, logging
        </p>
      </div>
    </div>
  );
}

function IntegrationsTable({ integrations, setIntegrations }) {
  const add = () => setIntegrations((a) => [...a, { id: crypto.randomUUID(), name: "", direction: "outbound", payload: "", auth: "OAuth2" }]);
  const update = (idx, patch) => setIntegrations((arr) => arr.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const remove = (idx) => setIntegrations((arr) => arr.filter((_, i) => i !== idx));
  return (
    <div className="space-y-3">
      <ToolbarButton variant="primary" onClick={add}>
        + Add Integration
      </ToolbarButton>
      {integrations.map((row, idx) => (
        <div key={row.id} className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Integration Name</label>
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g., HR System, CRM API"
                value={row.name}
                onChange={(e) => update(idx, { name: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Direction</label>
              <select
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={row.direction}
                onChange={(e) => update(idx, { direction: e.target.value })}
              >
                <option value="inbound">Inbound</option>
                <option value="outbound">Outbound</option>
                <option value="bidirectional">Bidirectional</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Payload Format</label>
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g., JSON, XML"
                value={row.payload}
                onChange={(e) => update(idx, { payload: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Authentication</label>
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g., OAuth2, API Key"
                value={row.auth}
                onChange={(e) => update(idx, { auth: e.target.value })}
              />
            </div>
          </div>
          <button
            onClick={() => remove(idx)}
            className="text-sm text-red-600 hover:text-red-700"
          >
            Remove Integration
          </button>
        </div>
      ))}
    </div>
  );
}
/* -------------------- WBS Draft (React 19 safe via react-complex-tree) -------------------- */
function WBSDraft({ treeData, setTreeData }) {
  // Convert simple tree [{ title, children: [...] }, ...] ⇄ react-complex-tree items map
  const buildItemsFromTree = (nodes, prefix = "n") => {
    const items = {};
    const ids = [];

    const walk = (arr, parentId) => {
      arr.forEach((node, i) => {
        const id = `${prefix}-${parentId ? parentId + "-" : ""}${i}-${Math.random().toString(36).slice(2, 7)}`;
        const childIds = [];
        items[id] = {
          index: id,
          isFolder: true,
          children: childIds,
          canMove: true,
          canRename: true,
          canDrag: true,
          data: { title: node.title ?? "Task" },
        };
        ids.push(id);
        if (node.children?.length) {
          const kids = walk(node.children, id);
          childIds.push(...kids);
        }
      });
      return ids.filter((x) => x.startsWith(`${prefix}-${parentId ? parentId + "-" : ""}`));
    };

    const topIds = walk(nodes, "");
    // root item required by the lib
    items.root = {
      index: "root",
      isFolder: true,
      children: topIds,
      canMove: false,
      canRename: false,
      canDrag: false,
      data: { title: "WBS" },
    };
    return items;
  };

  const itemsToTree = (items) => {
    const build = (id) => {
      const item = items[id];
      if (!item) return null;
      return {
        title: item.data?.title ?? "Task",
        children: (item.children || []).map(build).filter(Boolean),
      };
    };
    const root = items["root"];
    if (!root) return [];
    return (root.children || []).map(build);
  };

  // Local item state reflects the canonical WBS in the tree lib
  const [items, setItems] = React.useState(() => buildItemsFromTree(treeData));

  // Data provider (the lib calls onChange with the new items map after DnD/rename)
  const dataProvider = React.useMemo(
    () =>
      new StaticTreeDataProvider(
        items,
        (item) => item.data.title,
        (newItems) => setItems(newItems)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items] // recreate when items change (ok for uncontrolled environment)
  );

  // Keep parent state (treeData) in sync for saving/preview
  React.useEffect(() => {
    setTreeData(itemsToTree(items));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const [selectedId, setSelectedId] = React.useState(null);
  const [expandedItems, setExpandedItems] = React.useState(['root']);

  // Toolbar actions
  const addChild = () => {
    const target = selectedId || "root";
    const newId = `n-${Math.random().toString(36).slice(2, 8)}`;
    const draft = { ...items };
    draft[newId] = {
      index: newId,
      isFolder: true,
      children: [],
      canMove: true,
      canRename: true,
      canDrag: true,
      data: { title: "New task" },
    };
    draft[target] = {
      ...draft[target],
      children: [...(draft[target]?.children || []), newId],
    };
    setItems(draft);
  };

  const removeSelected = () => {
    const id = selectedId;
    if (!id || id === "root") return;
    const draft = { ...items };

    // remove from parent's children
    const parentId = Object.keys(draft).find((k) => draft[k].children?.includes(id));
    if (parentId) {
      draft[parentId] = {
        ...draft[parentId],
        children: draft[parentId].children.filter((c) => c !== id),
      };
    }
    // recursively delete the subtree
    const removeRec = (nodeId) => {
      const node = draft[nodeId];
      if (!node) return;
      (node.children || []).forEach(removeRec);
      delete draft[nodeId];
    };
    removeRec(id);

    setSelectedId(null);
    setItems(draft);
  };

  const renameSelected = () => {
    const id = selectedId;
    if (!id) return;
    const current = items[id]?.data?.title || "";
    const next = window.prompt("Rename task", current);
    if (next == null) return;
    setItems({
      ...items,
      [id]: { ...items[id], data: { ...items[id].data, title: next } },
    });
  };

  return (
    <div className="flex h-[500px] flex-col rounded-lg border border-gray-200 overflow-hidden">
      <div className="flex items-center gap-2 border-b border-gray-200 p-3 bg-gray-50">
        <button
          onClick={addChild}
          className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          + Add Child
        </button>
        <button
          onClick={renameSelected}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 disabled:opacity-50"
          disabled={!selectedId}
        >
          Rename
        </button>
        <button
          onClick={removeSelected}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
          disabled={!selectedId}
        >
          Remove
        </button>
        <div className="ml-auto text-xs text-gray-600">
          Click to select • Drag to reorder • F2 to rename
        </div>
      </div>

        <UncontrolledTreeEnvironment
            dataProvider={dataProvider}
            getItemTitle={(item) => item.data.title}
            canDragAndDrop
            canDropOnFolder
            canReorderItems
            defaultInteractionMode={InteractionMode.ClickItemToExpand}
            viewState={{
              'wbs': {
                expandedItems: expandedItems,
              }
            }}
            onExpandItem={(item) => setExpandedItems([...expandedItems, item.index])}
            onCollapseItem={(item) => setExpandedItems(expandedItems.filter(id => id !== item.index))}
        >
        <Tree
          treeId="wbs"
          rootItem="root"
          treeLabel="WBS"
          onPrimaryAction={(item) => setSelectedId(item.index)}
        />
      </UncontrolledTreeEnvironment>
    </div>
  );
}

/* -------------------- Live Markdown Preview -------------------- */
function SpecPreview({ markdown, onDownload }) {
  return (
    <div className="h-full overflow-auto rounded-2xl border border-gray-200 bg-white p-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-semibold">Live Spec Preview</div>
        <button onClick={onDownload} className="rounded-md border px-3 py-1 text-xs hover:bg-gray-50">Download .md</button>
      </div>
      <div className="prose max-w-none">
        <ReactMarkdown>{markdown || "*(empty)*"}</ReactMarkdown>
      </div>
    </div>
  );
}

/* -------------------- Main Inner -------------------- */
function BuilderInner({ demandId, discovery = {}, poc = {}, onSaved }) {
  const [frs, setFRs] = useState([]);
  const [nfrs, setNfrs] = useState([
    { id: crypto.randomUUID(), name: "availability", value: "99.9% monthly uptime" },
    { id: crypto.randomUUID(), name: "performance", value: "p95 < 500ms for key endpoints" },
    { id: crypto.randomUUID(), name: "security", value: "CIS baseline; OWASP ASVS L2" },
    { id: crypto.randomUUID(), name: "logging", value: "centralized audit logs; 90d retention" },
  ]);
  const [integrations, setIntegrations] = useState([]);
  const [treeData, setTreeData] = useState([{ title: "Initiation", children: [{ title: "Kickoff" }] }]);
  const [activeTab, setActiveTab] = useState("spec"); // spec | wbs | integrations
  const [savedVersion, setSavedVersion] = useState(0);
  const [loadError, setLoadError] = useState("");

  // Load the last saved spec. Without this the builder mounted empty every time,
  // so reopening the screen looked exactly like never having saved — and the four
  // seeded NFR rows below made it look like a fresh start rather than lost work.
  useEffect(() => {
    if (!demandId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const saved = await getRequirements(demandId);
        if (cancelled || !saved || !saved.version) return;
        // Restore ids: the tables key their rows on `id`, which the server doesn't store.
        if (saved.frs?.length) setFRs(saved.frs.map((f) => ({ ...f, id: f.id || crypto.randomUUID() })));
        if (saved.nfrs?.length) setNfrs(saved.nfrs.map((n) => ({ ...n, id: n.id || crypto.randomUUID() })));
        if (saved.integrations?.length) {
          setIntegrations(saved.integrations.map((i) => ({ ...i, id: i.id || crypto.randomUUID() })));
        }
        if (saved.wbs?.length) setTreeData(saved.wbs);
        setSavedVersion(saved.version);
        onSaved?.(saved.version);
      } catch (e) {
        if (!cancelled) setLoadError(e.message || String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [demandId, onSaved]);

  // AI ingest hook from interview
  const onIngestAI = (ai) => {
    if (ai?.fr) setFRs((a) => [...a, { id: crypto.randomUUID(), title: ai.fr, ac: [""] }]);
    if (ai?.nfr) setNfrs((a) => [...a, { id: crypto.randomUUID(), name: ai.nfr, value: "TBD" }]);
  };

  // Build markdown on the fly
  const markdown = useMemo(() => {
    const frLines = frs.map((f, i) => `| FR-${i + 1} | ${f.title} | ${(f.ac || []).filter(Boolean).join("; ")} |`).join("\n");
    const nfrLines = nfrs.map((n) => `| ${n.name} | ${n.value} |`).join("\n");
    const intLines = integrations.map((x) => `| ${x.name} | ${x.direction} | ${x.payload} | ${x.auth} |`).join("\n");
    return `# Requirements Specification — ${demandId}

## Functional Requirements
| ID | Requirement | Acceptance Criteria |
|---|---|---|
${frLines || "| — | — | — |"}

## Non-Functional Requirements
| Category | Requirement |
|---|---|
${nfrLines || "| — | — |"}

## Integrations
| Name | Direction | Payload | Auth |
|---|---|---|---|
${intLines || "| — | — | — | — |"}
`;
  }, [frs, nfrs, integrations, demandId]);

  // Validation before save/generate
  const validate = () => {
    const frViolations = frs.filter((f) => !f.ac?.some((a) => (a || "").trim()));
    const nfrSet = new Set(nfrs.map((n) => n.name.toLowerCase().trim()));
    const missingNfr = MIN_NFR_SET.filter((k) => !nfrSet.has(k));
    const badIntegrations = integrations.filter((i) => !(i.name && i.direction && i.payload && i.auth));
    const problems = [];
    if (frViolations.length) problems.push(`FRs missing AC: ${frViolations.length}`);
    if (missingNfr.length) problems.push(`Missing NFR categories: ${missingNfr.join(", ")}`);
    if (badIntegrations.length) problems.push(`Integrations missing fields: ${badIntegrations.length}`);
    return { ok: problems.length === 0, problems };
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      const spec = { frs, nfrs, integrations, wbs: treeData };
      const files = {
        requirementsSpecMd: markdown,
        requirementsJson: JSON.stringify(spec, null, 2),
        wbsJson: JSON.stringify(treeData, null, 2),
        traceabilityCsv: toCSV(
          frs.map((f, i) => ({
            demandId,
            artifact: `FR-${i + 1}`,
            requirement: f.title,
            source: f.source || "interview",
          }))
        ),
      };
      return saveRequirementsArtifacts(demandId, files);
    },
  });

  const generateMut = useMutation({
    mutationFn: async () => {
      const spec = { frs, nfrs, integrations };
      return runRequirementsAI(demandId, "GENERATE_SPEC", [{ role: "system", content: JSON.stringify(spec) }]);
    },
  });

  const onSave = async () => {
    const v = validate();
    if (!v.ok) {
      window.alert(`Fix before saving:\n- ${v.problems.join("\n- ")}`);
      return;
    }
    try {
      const res = await saveMut.mutateAsync();
      setSavedVersion(res?.version || savedVersion);
      onSaved?.(res?.version || savedVersion);
      window.alert(
        `Requirements spec v${res?.version} saved. Completeness: ${res?.completenessScore}%.`
      );
    } catch (e) {
      window.alert("Save failed: " + (e.message || e));
    }
  };

  const onGenerate = async () => {
    try {
      const res = await generateMut.mutateAsync();
      if (res?.fr) setFRs((a) => [...a, { id: crypto.randomUUID(), title: res.fr, ac: [""] }]);
      if (res?.nfr) setNfrs((a) => [...a, { id: crypto.randomUUID(), name: res.nfr, value: "TBD" }]);
      window.alert("AI generation complete (merged into tables).");
    } catch (e) {
      window.alert("Generation failed: " + (e.message || e));
    }
  };

  return (
  <div className="mx-auto max-w-7xl p-6">
    {/* Sticky header */}
    <div className="sticky top-0 z-10 -mx-6 mb-6 border-b border-gray-200 bg-white shadow-sm">
      <div className="mx-6 flex items-center justify-between py-4">
        <div>
          <p className="text-sm font-medium text-gray-700">
            Demand: <span className="font-mono text-indigo-600">{demandId}</span>
            {savedVersion > 0 && (
              <span className="ml-2 text-xs font-normal text-gray-500">
                loaded spec v{savedVersion}
              </span>
            )}
          </p>
          {loadError && (
            <p className="text-xs text-red-700">Could not load saved spec: {loadError}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <ToolbarButton variant="outline" onClick={onGenerate} disabled={generateMut.isLoading}>
            {generateMut.isLoading ? "Generating…" : "AI Generate"}
          </ToolbarButton>
          <ToolbarButton variant="primary" onClick={onSave} disabled={saveMut.isLoading}>
            {saveMut.isLoading ? "Saving…" : "Save Artifacts"}
          </ToolbarButton>
          <ToolbarButton
            variant="outline"
            onClick={() => download(`requirements-${demandId}.md`, markdown, "text/markdown")}
          >
            Export .md
          </ToolbarButton>
        </div>
      </div>
    </div>

    {/* Two-column work area */}
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Left column */}
      <div className="flex min-h-[76vh] flex-col gap-6">
        {/* Interview */}
        <Card title="Interview Console" subtitle="Ask about FRs, NFRs, acceptance criteria, integrations, scope…">
            <InterviewConsole demandId={demandId} onIngestAI={onIngestAI} />
        </Card>

        {/* Builder tabs */}
        <Card
          title="Requirements Builder"
          right={
            <div className="flex gap-2">
              {[
                { id: "spec", label: "Spec" },
                { id: "wbs", label: "WBS" },
                { id: "integrations", label: "Integrations" }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cx(
                    "px-3 py-1.5 rounded-lg text-sm font-medium",
                    activeTab === tab.id
                      ? "bg-indigo-600 text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  )}
                  aria-pressed={activeTab === tab.id}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          }
        >
        {activeTab === "spec" && (
        <div className="flex-1 space-y-6">
            <div>
              <h4 className="text-base font-semibold text-gray-900 mb-3">Functional Requirements</h4>
              <FRTable frs={frs} setFRs={setFRs} />
            </div>
            <div>
              <h4 className="text-base font-semibold text-gray-900 mb-3">Non-Functional Requirements</h4>
              <NFRTable nfrs={nfrs} setNfrs={setNfrs} />
            </div>
        </div>
        )}

          {activeTab === "wbs" && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Drag tasks to reparent/reorder. Use the toolbar to add, rename, or remove tasks.
              </p>
              <WBSDraft treeData={treeData} setTreeData={setTreeData} />
            </div>
          )}

          {activeTab === "integrations" && (
            <div className="space-y-3">
              <IntegrationsTable integrations={integrations} setIntegrations={setIntegrations} />
            </div>
          )}
        </Card>
      </div>

      {/* Right column: Live preview only */}
      <div className="min-h-[76vh]">
        <Card
          title="Live Spec Preview"
          right={
            <ToolbarButton
              variant="outline"
              onClick={() => download(`requirements-${demandId}.md`, markdown, "text/markdown")}
            >
              Download .md
            </ToolbarButton>
          }
          className="sticky top-[70px]"
        >
          <div className="prose prose-indigo max-w-none">
            <ReactMarkdown components={{
                h1: ({node, ...props}) => <h1 className="text-base font-semibold" {...props} />,
                h2: ({node, ...props}) => <h2 className="text-sm font-semibold" {...props} />,

            }}>{markdown || "*(empty)*"}</ReactMarkdown>
          </div>
        </Card>
      </div>
    </div>

    {/* Tiny toast area */}
    {(saveMut.isSuccess || generateMut.isSuccess) && (
      <div className="pointer-events-none fixed bottom-4 left-1/2 z-50 -translate-x-1/2 transform">
        <div className="pointer-events-auto rounded-lg bg-gray-900/90 px-4 py-2 text-sm text-white shadow-lg">
          {saveMut.isSuccess ? "Artifacts saved." : "AI generation complete."}
        </div>
      </div>
    )}
  </div>
);

}

export default function RequirementsBuilder({ demandId, onSaved }) {
  const client = useMemo(() => new QueryClient(), []);
  return (
    <QueryClientProvider client={client}>
      <BuilderInner demandId={demandId} onSaved={onSaved} />
    </QueryClientProvider>
  );
}


